import { useState } from "react"
import { useRouter } from "next/navigation"
import type { ResidenceDayInfo } from "@/lib/maps/planningEngine"
import type { NoSlotResult } from "@/components/rdv/NoSlotButton"
import type { NoSlotRequest, RdvFormData, RdvFormErrors, Slot } from "../_types"
import { EMPTY_FORM } from "../_types"
import { groupSlotsByDay, validateStep1 } from "../_utils"

export type RdvFlow = ReturnType<typeof useRdvFlow>

export function useRdvFlow() {
  const router = useRouter()
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [isPro, setIsPro] = useState(false)
  const [form, setForm] = useState<RdvFormData>(EMPTY_FORM)
  const [errors, setErrors] = useState<RdvFormErrors>({})

  // Étape 2 — vérification propriétaire
  const [identifyToken, setIdentifyToken] = useState<string | null>(null)
  const [syndicName, setSyndicName] = useState("")
  const [syndicError, setSyndicError] = useState<string | null>(null)
  const [syndicRejected, setSyndicRejected] = useState(false)
  const [syndicLoading, setSyndicLoading] = useState(false)
  const [needsReviewMode, setNeedsReviewMode] = useState(false)

  // Étape 3 — créneaux
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [slots, setSlots] = useState<Slot[]>([])
  const [slotDuration, setSlotDuration] = useState(60)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [residenceDays, setResidenceDays] = useState<ResidenceDayInfo[]>([])
  const [residenceDayFilter, setResidenceDayFilter] = useState<string | null>(null)
  const [showFreeSlots, setShowFreeSlots] = useState(false)
  const [loadingSlotsForDate, setLoadingSlotsForDate] = useState(false)
  const [outOfZone, setOutOfZone] = useState(false)
  const [geocodingFailed, setGeocodingFailed] = useState(false)

  // "Aucun créneau ne me convient" — toujours FREE_ACCESS dans le flux classique
  const [noSlotResult, setNoSlotResult] = useState<NoSlotResult | null>(null)

  function set(field: keyof RdvFormData, value: string | boolean) {
    setForm((f) => ({ ...f, [field]: value }))
    setErrors((e) => ({ ...e, [field]: undefined }))
  }

  function switchProfile(pro: boolean) {
    setIsPro(pro)
    setErrors({})
  }

  function changeSyndicName(value: string) {
    setSyndicName(value)
    setSyndicError(null)
  }

  function retrySyndic() {
    setSyndicRejected(false)
    setSyndicName("")
  }

  function backToStep1() {
    setStep(1)
    setSyndicError(null)
    setSyndicRejected(false)
  }

  function backToStep2() {
    setStep(2)
    setSelectedSlot(null)
    setResidenceDayFilter(null)
    setShowFreeSlots(false)
  }

  function showAllSlots() {
    setResidenceDayFilter(null)
    setShowFreeSlots(true)
  }

  function buildSlotsParams(sessionTokenValue: string): URLSearchParams {
    const fullAddress = [form.address, form.postalCode, form.city].filter(Boolean).join(", ")
    const params = new URLSearchParams()
    if (fullAddress) params.set("address", fullAddress)
    if (form.type) params.set("type", form.type)
    params.set("sessionToken", sessionTokenValue)
    return params
  }

  async function goToStep2() {
    const errs = validateStep1(form, isPro)
    setErrors(errs)
    if (Object.keys(errs).length > 0) return
    setStep(2)
    // Identification silencieuse (anti-énumération : aucun retour visible)
    try {
      const res = await fetch("/api/rdv/identify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          isPro
            ? { phone: form.phone, companyName: form.companyName, address: form.address }
            : {
                phone: form.phone,
                firstName: form.firstName,
                lastName: form.lastName,
                address: form.address,
              }
        ),
      })
      if (res.ok) {
        const data = await res.json()
        setIdentifyToken(data.token)
      }
    } catch {
      // Échec silencieux — l'étape 2 affichera une erreur si le token est absent
    }
  }

  async function goToStep3() {
    if (!syndicName.trim()) {
      setSyndicError("Merci d'indiquer le nom de votre propriétaire ou bailleur")
      return
    }
    if (!identifyToken) {
      setSyndicError("Session expirée, veuillez recommencer depuis le début")
      return
    }

    setSyndicLoading(true)
    setSyndicError(null)
    setSyndicRejected(false)
    setNeedsReviewMode(false)

    try {
      const res = await fetch("/api/rdv/verify-syndic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: identifyToken, syndicName }),
      })
      const data = await res.json()

      if (res.status === 403) {
        setSyndicRejected(true)
        return
      }
      if (res.status === 401) {
        setSyndicError("Session expirée, veuillez recommencer depuis le début")
        return
      }
      if (!res.ok) {
        setSyndicError("Une erreur est survenue, veuillez réessayer")
        return
      }

      const verifiedToken: string = data.sessionToken
      setSessionToken(verifiedToken)
      if (data.result === "unregistered") setNeedsReviewMode(true)
      setStep(3)
      setLoadingSlots(true)

      const slotsRes = await fetch(`/api/rdv/slots?${buildSlotsParams(verifiedToken)}`)
      const slotsData = await slotsRes.json()
      setOutOfZone(!!slotsData.outOfZone)
      setGeocodingFailed(!!slotsData.geocodingFailed)
      setSlots(slotsData.slots ?? [])
      setSlotDuration(slotsData.slotDuration ?? 60)
      setResidenceDays(slotsData.residenceDays ?? [])
    } finally {
      setSyndicLoading(false)
      setLoadingSlots(false)
    }
  }

  async function selectResidenceDay(dayKey: string) {
    setResidenceDayFilter(dayKey)
    // Si le créneau n'est pas dans les slots déjà chargés, on re-fetche pour ce jour précis
    const alreadyLoaded = slots.some((s) => s.dayKey === dayKey)
    if (!alreadyLoaded && sessionToken) {
      setLoadingSlotsForDate(true)
      const params = buildSlotsParams(sessionToken)
      params.set("targetDate", dayKey)
      const res = await fetch(`/api/rdv/slots?${params}`)
      const data = await res.json()
      setSlots((prev) => [...prev, ...(data.slots ?? [])])
      setLoadingSlotsForDate(false)
    }
  }

  async function submit() {
    if (!selectedSlot || (!turnstileToken && !sessionToken)) return
    setSubmitError(null)
    setSubmitting(true)
    const bookingPayload = isPro
      ? {
          ...form,
          firstName: undefined,
          lastName: undefined,
          date: selectedSlot,
          sessionToken,
          turnstileToken,
        }
      : { ...form, companyName: undefined, date: selectedSlot, sessionToken, turnstileToken }
    const res = await fetch("/api/rdv", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(bookingPayload),
    })
    if (res.ok) {
      const data = await res.json()
      router.push(`/rdv/confirmation/${data.token}?new=1`)
    } else {
      const data = await res.json().catch(() => ({}))
      setSubmitError(
        (data as { error?: string }).error ?? "Une erreur est survenue, veuillez réessayer."
      )
      setSubmitting(false)
    }
  }

  function buildNoSlotPayload(): NoSlotRequest {
    return {
      noSlotAccessType: "FREE_ACCESS",
      companyName: isPro ? form.companyName : undefined,
      firstName: isPro ? undefined : form.firstName,
      lastName: isPro ? undefined : form.lastName,
      phone: form.phone,
      email: form.email,
      address: form.address,
      residenceName: form.residenceName,
      bat: form.bat,
      floor: form.floor,
      postalCode: form.postalCode,
      city: form.city,
      type: form.type || undefined,
      description: form.description,
      consent: form.consent,
      sessionToken,
    }
  }

  const slotsByDay = groupSlotsByDay(slots, residenceDayFilter)
  const progressPct = step === 1 ? 33 : step === 2 ? 66 : 100

  // Journée résidence la plus proche
  const firstResDay = residenceDays[0]
  const showResidenceDayBlock = residenceDays.length > 0 && !showFreeSlots && !residenceDayFilter

  return {
    step,
    progressPct,
    // Étape 1
    isPro,
    switchProfile,
    form,
    errors,
    set,
    goToStep2,
    // Étape 2
    syndicName,
    changeSyndicName,
    syndicError,
    syndicRejected,
    syndicLoading,
    retrySyndic,
    backToStep1,
    goToStep3,
    // Étape 3
    needsReviewMode,
    sessionToken,
    slots,
    slotsByDay,
    slotDuration,
    selectedSlot,
    setSelectedSlot,
    loadingSlots,
    loadingSlotsForDate,
    submitting,
    submitError,
    turnstileToken,
    setTurnstileToken,
    residenceDays,
    residenceDayFilter,
    firstResDay,
    showResidenceDayBlock,
    outOfZone,
    geocodingFailed,
    selectResidenceDay,
    showAllSlots,
    backToStep2,
    submit,
    noSlotResult,
    setNoSlotResult,
    buildNoSlotPayload,
  }
}
